import React, { HTMLProps, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Linkify from 'react-linkify';
import {
  EMBED,
  MerchantBookingPageApiResponse,
  NO_CAPACITY,
  UPLOAD_IMAGE,
} from '../../../../interfaces/merchants/bookings';

import BookingEvent from '../../../../domain/entities/BookingEvent';
import TimeSlot from '../../../../domain/entities/TimeSlot';
import implBookingPageRepository from '../../../../infrastructure/repositories/BookingPageRepository';
import implTimeSlotRepository from '../../../../infrastructure/repositories/TimeSlotRepository';
import StringUtils from '../../../../components/utils/StringUtils';
import MerchantTag from '../../common/tags/MerchantTag';
import css from './MerchantResourceSummaryModal.module.scss';
import Heading from '../../../../atoms/Heading';
import MerchantCourseDetailsStaffList from '../../resources/book/MerchantCourseDetailsStaffList';
import usePerformableStaffMembers from '../../../../hooks/api/usePerformableStaffMembers';
import { CalendarLoading } from '../CalendarLoading';
import dynamic from 'next/dynamic';
import { RSButton } from 'src/reservation-page/RSButton';

interface Props extends HTMLProps<{}> {
  handleClose: () => void;
  handleBook: (event: BookingEvent) => void;
  event: BookingEvent;
  merchantId: string;
}

const RichViewer = dynamic(() => import('../../../../components/RichViewer'), {
  ssr: false,
});

const MerchantResourceSummaryModal: React.FC<Props> = ({
  handleBook,
  handleClose,
  event,
  merchantId,
}) => {
  const intl = useIntl();
  const [bookingPage, setBookingPage] = useState<
    MerchantBookingPageApiResponse | undefined
  >(undefined);
  const [timeSlot, setTimeSlot] = useState<TimeSlot | undefined>(undefined);

  const { data } = usePerformableStaffMembers({
    merchantPublicId: merchantId,
    bookingPageId: event.bookingPageId,
  });
  const performableStaffMembers = data?.data; // dataのdataとかややこしい...
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const fetcher = () => {
      const bookingPageId = event.bookingPageId;
      const timeSlotId = event.pointer.slot;

      const fetchers = [
        implBookingPageRepository.Find(merchantId, bookingPageId),
        timeSlotId
          ? implTimeSlotRepository.Find(merchantId, bookingPageId, timeSlotId)
          : new Promise<undefined>((resolve) => resolve(undefined)), // 何もしないpromise
      ] as const;

      Promise.all(fetchers)
        .then((results) => {
          const newBookingPage = results[0];
          const newTimeSlot = results[1];

          setBookingPage(newBookingPage);
          setTimeSlot(newTimeSlot);
        })
        .catch((err) => {
          throw err;
        });
    };
    fetcher();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef &&
        !containerRef.current?.contains(event.target as HTMLElement)
      ) {
        handleClose();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.body.style.overflow = 'initial';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [event.bookingPageId, event.pointer.slot, handleClose, merchantId]);

  const intlPref = 'merchants.services.merchantFullCalendar.summary.';

  const isWaitingListAvailable =
    timeSlot &&
    bookingPage &&
    timeSlot.vacancy <= 0 &&
    bookingPage.waiting_list_provided &&
    !event.isRegisteredWaitingList;

  const isRegisteredWaitingList =
    timeSlot &&
    bookingPage &&
    timeSlot.vacancy <= 0 &&
    bookingPage.waiting_list_provided &&
    event.isRegisteredWaitingList;

  const isVacancyVisible =
    timeSlot &&
    bookingPage &&
    timeSlot.vacancy >= 1 &&
    bookingPage.vacancy_visibility;

  const BookingButton: React.FC<{ children: string; disabled?: boolean }> = ({
    children,
    disabled,
  }) => {
    const handleClickBookingButton = () => {
      handleBook(event);
    };

    return (
      <RSButton
        variant="basic"
        disabled={disabled}
        onClick={handleClickBookingButton}
      >
        {children}
      </RSButton>
    );
  };

  if (!bookingPage) {
    return (
      <div className={css.MerchantResourceSummaryModal}>
        {!bookingPage && <CalendarLoading />}
      </div>
    );
  }

  let lowestPrice: { isVisible: true; price: number } | { isVisible: false } = {
    isVisible: false,
  };

  if (bookingPage.lowest_price != null && bookingPage.lowest_price > 0) {
    lowestPrice = { isVisible: true, price: bookingPage.lowest_price };
  }

  return (
    <div className={css.MerchantResourceSummaryModal}>
      <section
        className={css.MerchantResourceSummaryModal__Container}
        ref={containerRef}
      >
        <section className={css.MerchantResourceSummaryModal__Main}>
          {bookingPage.cover_contents_type === UPLOAD_IMAGE &&
            bookingPage.primary_image &&
            bookingPage.primary_image.c800x420 && (
              <section className={css.MerchantResourceSummaryModal__CoverImage}>
                <img
                  src={bookingPage.primary_image.c800x420.url}
                  alt="merchant-primary"
                />
              </section>
            )}
          {bookingPage.cover_contents_type === EMBED &&
            bookingPage.cover_content &&
            bookingPage.cover_content.thumbnail_url && (
              <section
                className={css.MerchantResourceSummaryModal__Embed}
                dangerouslySetInnerHTML={{
                  __html: bookingPage.cover_content.embed,
                }}
                style={{
                  backgroundImage: `url('${bookingPage.cover_content.thumbnail_url}')`,
                }}
              />
            )}

          <section className={css.MerchantResourceSummaryModal__Body}>
            <div className={css.MerchantResourceSummaryModal__Head}>
              <div className={css.MerchantResourceSummaryModal__HeadDate}>
                {intl.formatDate(event.start, {
                  month: 'narrow',
                  day: 'numeric',
                  weekday: 'short',
                })}{' '}
                {timeSlot &&
                  `${intl.formatTime(event.start, {
                    hour: 'numeric',
                    minute: 'numeric',
                  })}
                    ~
                    ${intl.formatTime(event.end, {
                      hour: 'numeric',
                      minute: 'numeric',
                    })}`}
              </div>
              {bookingPage && timeSlot && (
                <div className={css.MerchantResourceSummaryModal__HeadNotice}>
                  {isWaitingListAvailable &&
                    intl.formatMessage({
                      id: intlPref + 'waitingListAvailable',
                    })}
                  {isRegisteredWaitingList &&
                    intl.formatMessage({
                      id: intlPref + 'isRegisteredWaitingList',
                    })}
                  {isVacancyVisible &&
                    bookingPage.vacancy_visibility === NO_CAPACITY &&
                    intl.formatMessage(
                      { id: intlPref + 'vacancy' },
                      { vacancy: timeSlot.vacancy },
                    )}
                </div>
              )}
            </div>
            <span
              className={css.MerchantResourceSummaryModal__ColorPalette}
              style={{ backgroundColor: event.color }}
            />
            <div className={css.MerchantResourceSummaryModal__Title}>
              {bookingPage.name}
            </div>
            <div className={css.MerchantResourceSummaryModal__Description}>
              {bookingPage.description ? (
                <RichViewer value={bookingPage.description} />
              ) : (
                bookingPage.description_text && (
                  <Linkify>
                    {StringUtils.simpleFormat(bookingPage.description_text)}
                  </Linkify>
                )
              )}
            </div>
            {performableStaffMembers && performableStaffMembers.length > 0 && (
              <div className={css.staffMembersWrap}>
                <Heading className={css.staffMemberSectionHeading}>
                  {intl.formatMessage({
                    id: 'books.formsLabel.staff',
                  })}
                </Heading>
                <MerchantCourseDetailsStaffList
                  staff={performableStaffMembers}
                />
              </div>
            )}
            <div className={css.marginTop40}>
              {lowestPrice.isVisible && (
                <div className={css.MerchantResourceSummaryModal__Price}>
                  <Heading className={css.priceSectionHeading}>
                    {intl.formatMessage({ id: intlPref + 'priceHeading' })}
                  </Heading>
                  <p>
                    {intl.formatMessage(
                      { id: intlPref + 'priceRange' },
                      {
                        price: intl.formatNumber(lowestPrice.price),
                      },
                    )}
                  </p>
                </div>
              )}
            </div>
            {bookingPage.tag_names.length > 0 && (
              <div className={css.MerchantResourceSummaryModal__Tags}>
                <p>
                  {bookingPage.tag_names.map((tag, i) => (
                    <MerchantTag key={i} status={tag} />
                  ))}
                </p>
              </div>
            )}
          </section>
        </section>
        <section className={css.MerchantResourceSummaryModal__Footer}>
          <div className="flex justify-end space-x-2">
            <div>
              <RSButton variant="outline" onClick={handleClose}>
                {intl.formatMessage({ id: intlPref + 'actions.close' })}
              </RSButton>
            </div>

            {timeSlot && timeSlot.vacancy > 0 && (
              <div>
                <BookingButton>
                  {intl.formatMessage({ id: intlPref + 'actions.book' })}
                </BookingButton>
              </div>
            )}
            {isWaitingListAvailable && (
              <div>
                <BookingButton>
                  {intl.formatMessage({ id: intlPref + 'actions.waitingList' })}
                </BookingButton>
              </div>
            )}
            {isRegisteredWaitingList && (
              <div>
                <BookingButton disabled>
                  {intl.formatMessage({
                    id: intlPref + 'actions.isRegisteredWaitingList',
                  })}
                </BookingButton>
              </div>
            )}
            {event.pointer.date && (
              <div>
                <BookingButton>
                  {intl.formatMessage({ id: intlPref + 'actions.book' })}
                </BookingButton>
              </div>
            )}
          </div>
        </section>
      </section>
    </div>
  );
};

export default MerchantResourceSummaryModal;
